/* Cart drawer flavor upsell.
 *
 * Adds one bag of the chosen flavor to the cart, but smart about what's
 * already there:
 *
 *   - Cart already has a peachy subscription bundle → upsell merges in,
 *     escalating the tier (1→2, 2→3, 3→4) with the matching selling plan
 *     and Loop _bundleId.
 *   - Cart already has peachy one-time bags → upsell joins as another
 *     one-time bag (no selling plan, no bundle).
 *   - Cart has neither → upsell adds as a tagged one-time bag so the next
 *     click can merge.
 *
 * Falls back to plain /cart/add.js if the buy-box section isn't on the
 * page (no bundle config available) — the merge logic needs the four
 * selling plan IDs and three bundle IDs which currently live as data
 * attributes on the bundle builder.
 */
(function () {
  if (window.__peachyUpsellBound) return;
  window.__peachyUpsellBound = true;

  var SRC_SUB = 'bundle-sub';
  var SRC_OTP = 'bundle-otp';

  function readBundleConfig() {
    var el = document.querySelector('[data-bundle]:not([data-bundle-arm])');
    if (el) {
      var sellingPlans = (el.dataset.sellingPlans || '').split(',');
      if (sellingPlans.length < 4) return null;
      // NOTE: no bundleIds. The Loop _bundleId is never read from a static
      // source — it is freshly minted per add via window.PeachyLoopMint.
      return {
        sellingPlans: sellingPlans,
        bundleNames: (el.dataset.bundleNames || '').split('|'),
        maxQty: parseInt(el.dataset.maxQty, 10) || 4
      };
    }
    // No buy box on this page — fall back to the config the buy box persisted
    // to localStorage (peachy-cart-bundle-sync.js), so the upsell works on
    // every page. The Loop _bundleId is still minted fresh, never stored.
    var stored = (window.PeachyBundleStore && window.PeachyBundleStore.load) ? window.PeachyBundleStore.load() : null;
    if (stored && stored.sellingPlans && stored.sellingPlans.length >= 4) {
      return { sellingPlans: stored.sellingPlans, bundleNames: stored.bundleNames || [], maxQty: stored.maxQty || 4 };
    }
    return null;
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function clearKeys(keys) {
    if (!keys.length) return Promise.resolve();
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
  }

  function addItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    });
  }

  // bundleTxnId is a freshly-minted Loop transaction id (or '' for one-time
  // / single-bag lines). It is the ONLY source of _bundleId — never static.
  function buildItemsFor(qtyByVariant, srcTag, config, bundleTxnId) {
    var total = 0;
    Object.keys(qtyByVariant).forEach(function (k) { total += qtyByVariant[k]; });
    if (total === 0) return { items: [], total: 0 };

    var withSellingPlan = srcTag === SRC_SUB;
    var sellingPlan = withSellingPlan ? config.sellingPlans[total - 1] : '';
    var bundleName = (withSellingPlan && total >= 2) ? (config.bundleNames[total - 1] || '') : '';

    var items = Object.keys(qtyByVariant).map(function (vid) {
      var item = { id: parseInt(vid, 10), quantity: qtyByVariant[vid] };
      if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);
      var props = { _peachySrc: srcTag };
      if (bundleTxnId) props._bundleId = bundleTxnId;
      if (bundleName) props._bundleName = bundleName;
      item.properties = props;
      return item;
    });

    return { items: items, total: total };
  }

  async function refreshDrawer() {
    var res = await fetch(window.location.pathname + window.location.search, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    var html = await res.text();
    var doc = new DOMParser().parseFromString(html, 'text/html');

    var nextInner = doc.querySelector('cart-drawer .drawer__inner');
    var currentInner = document.querySelector('cart-drawer .drawer__inner');
    if (nextInner && currentInner) {
      currentInner.innerHTML = nextInner.innerHTML;
    }

    var nextDrawer = doc.querySelector('cart-drawer');
    var currentDrawer = document.querySelector('cart-drawer');
    if (nextDrawer && currentDrawer) {
      currentDrawer.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
    }

    var nextBubble = doc.getElementById('cart-icon-bubble');
    var currentBubble = document.getElementById('cart-icon-bubble');
    if (nextBubble && currentBubble) {
      currentBubble.innerHTML = nextBubble.innerHTML;
    }

    try {
      var cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      var cart = await cartRes.json();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'peachy-upsell', cartData: cart });
      }
    } catch (_) {}

    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  async function handleSubmit(form, btn) {
    if (btn) btn.classList.add('is-loading');

    try {
      var data = new FormData(form);
      var variantId = parseInt(data.get('id'), 10);
      var quantity = parseInt(data.get('quantity'), 10) || 1;

      var config = readBundleConfig();

      if (!config) {
        // Plain add. Tag as otp so a future merge has a hook.
        await addItems([{ id: variantId, quantity: quantity, properties: { _peachySrc: SRC_OTP } }]);
        await refreshDrawer();
        return;
      }

      var cart = await fetchCart();
      var items = cart.items || [];
      var subItems = items.filter(function (it) { return it.properties && it.properties._peachySrc === SRC_SUB; });
      var otpItems = items.filter(function (it) { return it.properties && it.properties._peachySrc === SRC_OTP; });

      // Subscription cart wins if both present (shouldn't happen in normal flow).
      var existing, srcTag;
      if (subItems.length > 0) {
        existing = subItems;
        srcTag = SRC_SUB;
      } else if (otpItems.length > 0) {
        existing = otpItems;
        srcTag = SRC_OTP;
      } else {
        existing = [];
        srcTag = SRC_OTP;
      }

      // Merge: existing peachy line items + 1 of the upsell variant.
      var qtyByVariant = {};
      existing.forEach(function (it) {
        qtyByVariant[it.variant_id] = (qtyByVariant[it.variant_id] || 0) + it.quantity;
      });
      qtyByVariant[variantId] = (qtyByVariant[variantId] || 0) + quantity;

      // Compute the merged total up front so we can cap and mint BEFORE
      // touching the cart.
      var mergedTotal = 0;
      Object.keys(qtyByVariant).forEach(function (k) { mergedTotal += qtyByVariant[k]; });

      if (mergedTotal > config.maxQty) {
        // Hard cap. Best UX for now: don't add, surface via console + button reset.
        // The bundle builder shows an inline error; the upsell card has no error
        // slot, so we just no-op silently and let the user manage their cart.
        if (btn) btn.classList.remove('is-loading');
        return;
      }

      // Mint a FRESH, unique Loop transaction for tier 2+ subscription
      // bundles. Minted before clearing the existing lines, so if it fails
      // the cart is untouched and no static/blank id is ever written.
      var bundleTxnId = '';
      if (srcTag === SRC_SUB && mergedTotal >= 2) {
        if (!window.PeachyLoopMint || typeof window.PeachyLoopMint.mint !== 'function') {
          console.error('[peachy-upsell] PeachyLoopMint unavailable; aborting add (cart unchanged).');
          if (btn) btn.classList.remove('is-loading');
          return;
        }
        var productVariants = Object.keys(qtyByVariant).map(function (vid) {
          return { shopifyId: parseInt(vid, 10), quantity: qtyByVariant[vid] };
        });
        try {
          bundleTxnId = await window.PeachyLoopMint.mint({
            productVariants: productVariants,
            total: mergedTotal,
            sellingPlanShopifyId: config.sellingPlans[mergedTotal - 1] ? parseInt(config.sellingPlans[mergedTotal - 1], 10) : null
          });
        } catch (mintErr) {
          console.error('[peachy-upsell] Loop mint failed; aborting add (cart unchanged).', mintErr);
          if (btn) btn.classList.remove('is-loading');
          return;
        }
      }

      var built = buildItemsFor(qtyByVariant, srcTag, config, bundleTxnId);

      // Clear existing peachy lines, then add the merged total at the right tier.
      var keys = existing.map(function (it) { return it.key; });
      await clearKeys(keys);
      await addItems(built.items);
      await refreshDrawer();
    } catch (err) {
      console.error('[peachy-upsell] add failed', err);
      if (btn) btn.classList.remove('is-loading');
    }
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-peachy-upsell-form]');
    if (!form) return;
    e.preventDefault();
    var btn = form.querySelector('.peachy-cart-upsell__add');
    handleSubmit(form, btn);
  });
})();
